import clsx from 'clsx'
import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {LoginMobile} from '../../auth/components/LoginMobile'
import {goToGateway, readApiError, rial} from '../../online-payment/core/onlinePaymentApi'
import {hidesPayments, STORE_BUILD_UNAVAILABLE_TEXT} from '../../install-app/core/androidApp'
import {CategoryAccess, CategoryLockState, CategoryOffer, getCategoryAccess, startCategorySubscription} from './stuffidAccessApi'

/** The panel the business turns the service on in (an absolute address: this page may be on stuffid.asatsp.ir). */
const PANEL_URL = window.location.hostname.startsWith('stuffid.') ? 'https://my.asatsp.ir' : ''

const offerKey = (offer: CategoryOffer) => (offer.months === null || offer.months === undefined ? 'ever' : String(offer.months))

/**
 * Where the results of a search by category would be, when the asker may not browse by category (user request
 * 2026-10-18: «شناسه کالا/خدمت قسمت دسته بندی پولی بشه چه برای کسب و کارها … چه بدون لاگین که با موبایل باید لاگین کنن»):
 * signing in with a mobile, then turning the service on for the business (in the panel) or buying a subscription of
 * one's own through the gateway. Plain search stays free and is offered as the way out.
 */
export const StuffIdCategoryLock: FC<{
  state: CategoryLockState
  message: string
  /** The categories are open now (signed in with access, or bought): the search runs again. */
  onOpened: () => void
  /** Clears the category so the plain search shows. */
  onClearCategory: () => void
}> = ({state, message, onOpened, onClearCategory}) => {
  const [access, setAccess] = useState<CategoryAccess>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [picked, setPicked] = useState<string>()
  const [buying, setBuying] = useState(false)
  // The page passes a fresh function on every render; the read uses the latest one without running again.
  const onOpenedRef = useRef(onOpened)
  onOpenedRef.current = onOpened

  const read = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const next = await getCategoryAccess()
      setAccess(next)
      if (next.open) onOpenedRef.current()
    } catch (readError) {
      setError(readApiError(readError, 'وضعیت دسترسی خوانده نشد؛ دوباره تلاش کنید.').message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    read()
  }, [read, state])

  // Nothing to activate or buy in the Bazaar and Myket builds of the Android app: the lock only says so.
  const noPayments = hidesPayments()
  const signIn = (access ? !access.signedIn : state === 'signIn')
  const offers = access?.offers || []
  const chosen = offers.find((offer) => offerKey(offer) === picked) || offers[offers.length - 1]

  const buy = async () => {
    if (!chosen) return
    setBuying(true)
    setError('')
    try {
      const started = await startCategorySubscription(chosen.months, window.location.pathname + window.location.search)
      goToGateway(started.redirect)
      // The page is leaving for the gateway; the button stays busy until it does.
    } catch (buyError) {
      setError(readApiError(buyError, 'شروع پرداخت انجام نشد؛ دوباره تلاش کنید.').message)
      setBuying(false)
    }
  }

  return (
    <section className='sdp-lock' aria-labelledby='sdp-lock-title'>
      <div className='sdp-lock__head'>
        <span className='sdp-lock__icon' aria-hidden='true'>
          <i className='las la-lock' />
        </span>
        <div>
          <h2 id='sdp-lock-title' className='sdp-lock__title'>
            دسته‌بندی شناسه‌ها سرویس ویژه است
          </h2>
          <p className='sdp-lock__text'>{(noPayments && !signIn && STORE_BUILD_UNAVAILABLE_TEXT) || access?.reason || message}</p>
        </div>
      </div>

      {loading && !access && <span className='spinner-border spinner-border-sm text-primary' aria-label='در حال دریافت' />}

      {signIn && (
        <div className='sdp-lock__part sdp-lock__part--sign-in'>
          <h3 className='sdp-lock__subtitle'>ورود با شمارهٔ همراه</h3>
          <LoginMobile simpleMode successFn={read} />
        </div>
      )}

      {!signIn && access && !noPayments && (
        <>
          {access.business && (
            <div className='sdp-lock__part'>
              <h3 className='sdp-lock__subtitle'>برای کسب‌وکار شما</h3>
              <p className='mb-2'>
                سرویس «دسته‌بندی شناسه کالا/خدمت» را در «سرویس‌ها و تعرفه‌ها» فعال کنید تا همهٔ کاربران کسب‌وکار
                از آن استفاده کنند؛ هزینه از کیف پول کسب‌وکار کم می‌شود.
              </p>
              {access.business.description && <p className='text-muted fs-7 mb-2'>تعرفه: {access.business.description}</p>}
              <a className='btn btn-sm btn-light-primary' href={`${PANEL_URL}${access.business.servicesPath}`} target='_blank' rel='noreferrer'>
                <i className='las la-external-link-alt' aria-hidden='true' />
                سرویس‌ها و تعرفه‌های کسب‌وکار
              </a>
            </div>
          )}

          {offers.length > 0 ? (
            <div className='sdp-lock__part'>
              <h3 className='sdp-lock__subtitle'>اشتراک شخصی</h3>
              <fieldset className='sdp-lock__offers' disabled={buying}>
                <legend className='visually-hidden'>دورهٔ اشتراک</legend>
                {offers.map((offer) => {
                  const key = offerKey(offer)
                  const on = chosen && offerKey(chosen) === key
                  return (
                    <label key={key} className={clsx('sdp-lock__offer', {'is-on': on})}>
                      <input type='radio' name='sdp-offer' className='form-check-input' checked={Boolean(on)} onChange={() => setPicked(key)} />
                      <span className='sdp-lock__offer-text'>
                        <span className='fw-bold'>{offer.title}</span>
                        <span>
                          {offer.listAmount && offer.listAmount > offer.amount && (
                            <s className='text-muted me-1'>{rial(offer.listAmount)}</s>
                          )}
                          {rial(offer.payable)} ریال
                        </span>
                        {offer.vat > 0 && (
                          <span className='text-muted fs-8'>
                            {rial(offer.amount)} ریال و {rial(offer.vat)} ریال مالیات بر ارزش افزوده
                          </span>
                        )}
                      </span>
                    </label>
                  )
                })}
              </fieldset>
              <button type='button' className='btn btn-primary mt-3' onClick={buy} disabled={buying || !chosen}>
                {buying ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
                    در حال انتقال به درگاه…
                  </>
                ) : (
                  <>پرداخت {chosen ? `${rial(chosen.payable)} ریال` : ''} و فعال‌سازی</>
                )}
              </button>
              <p className='text-muted fs-8 mt-2 mb-0'>
                <i className='las la-lock me-1' aria-hidden='true' />
                پرداخت در صفحهٔ امن درگاه بانکی انجام می‌شود و صورتحساب آن به نام شما صادر می‌شود.
              </p>
            </div>
          ) : (
            !access.business && (
              <p className='sdp-lock__part mb-0'>این سرویس فعلاً برای کسب‌وکارها فعال می‌شود؛ برای خرید، از پنل معتمد آسا اقدام کنید.</p>
            )
          )}
        </>
      )}

      {error && (
        <div className='alert alert-danger py-2 mt-3 mb-0' role='alert'>
          {error}
          <button type='button' className='btn btn-sm btn-link' onClick={read}>
            تلاش مجدد
          </button>
        </div>
      )}

      <button type='button' className='btn btn-sm btn-link px-0 mt-3' onClick={onClearCategory}>
        جست‌وجوی ساده بدون دسته‌بندی (رایگان)
      </button>
    </section>
  )
}
